import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, Copy, ExternalLink, RefreshCw, Search, Sparkles, Loader2 } from "lucide-react";

const SKIN_COLORS = {
    dry: "text-amber-300 border-amber-300/40 bg-amber-300/10",
    oily: "text-cyan-300 border-cyan-300/40 bg-cyan-300/10",
    combination: "text-fuchsia-300 border-fuchsia-300/40 bg-fuchsia-300/10",
    normal: "text-emerald-300 border-emerald-300/40 bg-emerald-300/10",
    sensitive: "text-rose-300 border-rose-300/40 bg-rose-300/10",
};

function fmtDate(iso) {
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function AdminLeads() {
    const { user, loading } = useAuth();
    const [leads, setLeads] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all");
    const [aiLead, setAiLead] = useState(null);      // lead currently open in AI panel
    const [aiInbound, setAiInbound] = useState("");
    const [aiOut, setAiOut] = useState("");
    const [aiBusy, setAiBusy] = useState(false);

    const load = async () => {
        setFetching(true);
        try {
            const { data } = await api.get("/admin/leads");
            setLeads(data.leads || []);
        } catch (err) {
            console.error("leads load failed:", err);
            toast.error("Couldn't load leads");
        }
        setFetching(false);
    };

    useEffect(() => {
        if (loading) return;
        if (user?.role !== "admin") return; // silently skip fetch for non-admins (UI guard shows below)
        load();
    }, [loading, user]);

    const stats = useMemo(() => {
        const total = leads.length;
        const registered = leads.filter((l) => l.is_registered).length;
        const converted = leads.filter((l) => l.subscription_status === "active").length;
        const bySource = {};
        leads.forEach((l) => {
            const s = (l.source || "direct").toLowerCase();
            bySource[s] = (bySource[s] || 0) + 1;
        });
        return { total, registered, converted, bySource };
    }, [leads]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return leads.filter((l) => {
            if (filter === "unregistered" && l.is_registered) return false;
            if (filter === "registered" && !l.is_registered) return false;
            if (filter === "converted" && l.subscription_status !== "active") return false;
            if (filter.startsWith("src:") && (l.source || "direct").toLowerCase() !== filter.slice(4)) return false;
            if (!q) return true;
            return (
                (l.client_name || "").toLowerCase().includes(q) ||
                (l.client_email || "").toLowerCase().includes(q) ||
                (l.client_phone || "").toLowerCase().includes(q) ||
                (l.skin_type || "").toLowerCase().includes(q) ||
                (l.source || "").toLowerCase().includes(q)
            );
        });
    }, [leads, filter, query]);

    if (loading) return <div className="p-10 text-center text-white/50">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "admin") {
        return (
            <div className="max-w-xl mx-auto mt-10 p-8 rounded-2xl border border-rose/30 bg-rose/5 text-center">
                <p className="font-serif text-2xl">Admin only</p>
                <p className="text-white/60 mt-2">You don&apos;t have access to the leads dashboard.</p>
                <Link to="/portal" className="inline-block mt-5 text-rose hover:underline">← Back to portal</Link>
            </div>
        );
    }

    const copyEmails = () => {
        const emails = filtered.map((l) => l.client_email).filter(Boolean).join(", ");
        if (!emails) return toast.error("No emails to copy");
        navigator.clipboard.writeText(emails);
        toast.success(`Copied ${filtered.length} email${filtered.length === 1 ? "" : "s"}`);
    };

    const openAiSms = (lead) => {
        setAiLead(lead);
        setAiInbound("");
        setAiOut("");
    };

    const generateAiSms = async () => {
        if (!aiInbound.trim()) return toast.error("Paste what the lead just texted you first");
        setAiBusy(true);
        try {
            const { data } = await api.post("/ai/sms-lead-reply", {
                inbound: aiInbound,
                lead_email: aiLead?.client_email,
            });
            setAiOut(data.reply || "");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "AI unavailable");
        }
        setAiBusy(false);
    };

    const copyAiSms = () => {
        navigator.clipboard.writeText(aiOut);
        toast.success("Copied — paste into your Messages app");
    };

    const smsLink = (phone, name, facial) => {
        const body = encodeURIComponent(
            `Hi ${name?.split(" ")[0] || "gorgeous"} 💛 It's 24K Glow On The Go — I saw you took our skin quiz! ` +
            `Your match: ${facial || "your perfect facial"}. First-timers get 50% off — reply YES and we'll book you in.`
        );
        return `sms:${(phone || "").replace(/[^\d+]/g, "")}?&body=${body}`;
    };

    const emailLink = (email, name, facial) => {
        const subject = encodeURIComponent("Your 24K Glow skin quiz result ✦");
        const body = encodeURIComponent(
            `Hi ${name?.split(" ")[0] || "there"},\n\nThanks for taking our skin quiz! ` +
            `Based on your answers, I'd recommend the ${facial || "Signature Glow Facial"}.\n\n` +
            `As a first-timer, you can claim 50% off your first month — just book at ` +
            `https://monthlyglowonthego.com/portal/book\n\n` +
            `Same-day service guaranteed if you book before 6pm. Any questions, just reply to this email.\n\n` +
            `✦ 24K Glow On The Go\nGlam on Wheels · Jacksonville · St. Augustine · Orange Park`
        );
        return `mailto:${email}?subject=${subject}&body=${body}`;
    };

    return (
        <div className="max-w-[1300px] mx-auto" data-testid="admin-leads-page">
            <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <Link to="/portal" className="text-xs text-white/55 hover:text-white inline-flex items-center gap-1 mb-2">
                        <ArrowLeft className="w-3 h-3" /> Back to portal
                    </Link>
                    <p className="text-xs tracking-[0.3em] uppercase text-gold">Admin</p>
                    <h1 className="font-serif text-4xl sm:text-5xl mt-1">Leads</h1>
                    <p className="mt-2 text-white/55 max-w-xl">
                        Everyone who took the skin quiz. Follow up by SMS or email — hot leads convert best within 24 hours.
                    </p>
                </div>
                <button
                    onClick={load}
                    data-testid="leads-refresh-btn"
                    className="px-4 py-2 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition flex items-center gap-2 text-sm"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
            </div>

            {/* Stats */}
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <StatCard label="Total leads" value={stats.total} accent="text-gold" />
                <StatCard label="Registered" value={stats.registered} accent="text-rose" />
                <StatCard label="Converted (paid)" value={stats.converted} accent="text-emerald-300" />
            </div>

            {/* Sources — click to filter */}
            {Object.keys(stats.bySource).length > 0 && (
                <div className="mb-6 flex flex-wrap gap-2 items-center">
                    <span className="text-[10px] uppercase tracking-widest text-white/40 mr-2">By source:</span>
                    {Object.entries(stats.bySource)
                        .sort((a, b) => b[1] - a[1])
                        .map(([src, count]) => (
                            <button
                                key={src}
                                type="button"
                                onClick={() => setFilter(filter === `src:${src}` ? "all" : `src:${src}`)}
                                data-testid={`source-chip-${src}`}
                                className={`px-3 py-1.5 rounded-full text-xs transition border ${
                                    filter === `src:${src}`
                                        ? "border-gold bg-gold/15 text-gold"
                                        : "border-white/10 text-white/60 hover:border-white/25"
                                }`}
                            >
                                {src === "instagram" && "📸 "}
                                {src === "facebook" && "🅵 "}
                                {src === "google" && "🔎 "}
                                <span className="capitalize">{src}</span>
                                <span className="ml-1.5 text-white/40">{count}</span>
                            </button>
                        ))}
                </div>
            )}

            {/* Filters */}
            <div className="mb-6 flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by name, email, phone, skin type…"
                        data-testid="leads-search"
                        className="w-full pl-10 pr-3 py-2.5 rounded-full bg-surface border border-white/10 focus:border-gold/50 outline-none text-sm transition"
                    />
                </div>
                <div className="flex gap-1 bg-surface border border-white/10 rounded-full p-1 text-xs">
                    {[
                        { k: "all", label: "All" },
                        { k: "unregistered", label: "Not signed up" },
                        { k: "registered", label: "Signed up" },
                        { k: "converted", label: "Paying" },
                    ].map((f) => (
                        <button
                            key={f.k}
                            onClick={() => setFilter(f.k)}
                            data-testid={`leads-filter-${f.k}`}
                            className={`px-3 py-1.5 rounded-full transition ${
                                filter === f.k ? "bg-gold text-ink" : "text-white/60 hover:text-white"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <button
                    onClick={copyEmails}
                    data-testid="leads-copy-emails"
                    className="px-4 py-2 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink transition flex items-center gap-2 text-sm"
                >
                    <Copy className="w-3.5 h-3.5" /> Copy emails
                </button>
            </div>

            {/* Table */}
            <div className="rounded-2xl border border-white/10 bg-surface overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
                    <div className="col-span-3">Client</div>
                    <div className="col-span-3">Contact</div>
                    <div className="col-span-2">Skin type</div>
                    <div className="col-span-2">Recommended</div>
                    <div className="col-span-1">Status</div>
                    <div className="col-span-1 text-right">Actions</div>
                </div>
                {fetching ? (
                    <div className="p-12 text-center text-white/40">Loading leads…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-16 text-center">
                        <p className="font-serif text-2xl text-white/60">No leads yet</p>
                        <p className="text-sm text-white/40 mt-2">
                            Share the quiz link on Instagram: <a href="https://www.instagram.com/christinamariesunshine/" target="_blank" rel="noreferrer" className="text-gold hover:underline">@christinamariesunshine</a> → link in bio: <span className="text-gold">monthlyglowonthego.com/skin-quiz</span>
                        </p>
                    </div>
                ) : (
                    filtered.map((l) => (
                        <LeadRow key={l.assessment_id} lead={l} smsLink={smsLink} emailLink={emailLink} onAiSms={openAiSms} />
                    ))
                )}
            </div>

            <p className="mt-8 text-xs text-white/40 text-center">
                Follow-up idea: text new leads within 24 hours — conversion rate is ~10× higher.
            </p>

            {/* AI SMS drafter modal */}
            {aiLead && (
                <div
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setAiLead(null)}
                    data-testid="ai-sms-modal"
                >
                    <div
                        className="w-full max-w-lg rounded-2xl border border-gold/40 bg-ink/95 p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <p className="text-xs tracking-widest text-gold uppercase">AI SMS reply drafter</p>
                                <p className="font-serif text-xl mt-1">{aiLead.client_name || aiLead.client_email}</p>
                                <p className="text-xs text-white/50">{aiLead.client_phone || "no phone on file"}</p>
                            </div>
                            <button onClick={() => setAiLead(null)} className="text-white/50 hover:text-white transition text-2xl">×</button>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-widest text-white/45 mb-2">What did they text you?</p>
                            <textarea
                                value={aiInbound}
                                onChange={(e) => setAiInbound(e.target.value)}
                                placeholder="Paste their message here…"
                                rows={3}
                                data-testid="ai-sms-inbound"
                                className="w-full bg-surface border border-white/10 focus:border-gold/50 rounded-xl px-4 py-3 outline-none text-sm resize-none"
                            />
                        </div>
                        <button
                            onClick={generateAiSms}
                            disabled={aiBusy || !aiInbound.trim()}
                            data-testid="ai-sms-generate"
                            className="mt-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-gold via-rose to-gold text-ink font-medium text-sm hover:opacity-90 transition disabled:opacity-50 flex items-center gap-2"
                        >
                            {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                            Draft reply
                        </button>
                        {aiOut && (
                            <div className="mt-4 relative p-4 rounded-xl bg-ink/60 border border-gold/30" data-testid="ai-sms-output">
                                <button onClick={copyAiSms} title="Copy" className="absolute top-2 right-2 p-1.5 text-white/55 hover:text-gold transition">
                                    <Copy className="w-3.5 h-3.5" />
                                </button>
                                <p className="text-sm text-white pr-8 leading-relaxed">{aiOut}</p>
                                <p className="text-[10px] text-white/40 mt-2">{aiOut.length} / 160 chars</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, accent }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-surface p-5">
            <p className="text-[10px] uppercase tracking-widest text-white/45">{label}</p>
            <p className={`font-serif text-4xl mt-2 ${accent}`}>{value}</p>
        </div>
    );
}

function LeadRow({ lead, smsLink, emailLink, onAiSms }) {
    const skinClass = SKIN_COLORS[lead.skin_type] || "text-white/70 border-white/10 bg-white/5";
    return (
        <div
            data-testid={`lead-${lead.assessment_id}`}
            className="grid md:grid-cols-12 gap-3 px-5 py-4 border-t border-white/5 items-center text-sm hover:bg-white/[0.02] transition"
        >
            <div className="md:col-span-3">
                <p className="font-serif text-base">{lead.client_name || "—"}</p>
                <p className="text-xs text-white/40">{fmtDate(lead.created_at)}</p>
            </div>
            <div className="md:col-span-3 text-white/70 text-xs space-y-0.5 min-w-0">
                <p className="truncate">{lead.client_email}</p>
                <p className="text-white/50">{lead.client_phone || "no phone"}</p>
            </div>
            <div className="md:col-span-2">
                <span className={`inline-block px-2.5 py-1 rounded-full border text-xs capitalize ${skinClass}`}>
                    {lead.skin_type}
                </span>
                {lead.source && lead.source !== "direct" && (
                    <span className="ml-1.5 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border border-gold/30 text-gold/80">
                        {lead.source}
                    </span>
                )}
            </div>
            <div className="md:col-span-2 text-xs text-white/70 truncate" title={lead.recommended_facial || ""}>
                {lead.recommended_facial || "—"}
            </div>
            <div className="md:col-span-1">
                {lead.subscription_status === "active" ? (
                    <span className="text-xs text-emerald-300">✦ Paying</span>
                ) : lead.is_registered ? (
                    <span className="text-xs text-rose">Signed up</span>
                ) : (
                    <span className="text-xs text-white/40">Lead</span>
                )}
            </div>
            <div className="md:col-span-1 flex justify-end gap-1">
                <button
                    type="button"
                    onClick={() => onAiSms(lead)}
                    title="AI draft SMS reply"
                    data-testid={`lead-ai-${lead.assessment_id}`}
                    className="p-2 rounded-full text-white/55 hover:text-gold hover:bg-white/5 transition"
                >
                    <Sparkles className="w-4 h-4" />
                </button>
                {lead.client_phone && (
                    <a
                        href={smsLink(lead.client_phone, lead.client_name, lead.recommended_facial)}
                        title="Text this lead"
                        data-testid={`lead-sms-${lead.assessment_id}`}
                        className="p-2 rounded-full text-white/55 hover:text-gold hover:bg-white/5 transition"
                    >
                        <Phone className="w-4 h-4" />
                    </a>
                )}
                {lead.client_email && (
                    <a
                        href={emailLink(lead.client_email, lead.client_name, lead.recommended_facial)}
                        title="Email this lead"
                        data-testid={`lead-email-${lead.assessment_id}`}
                        className="p-2 rounded-full text-white/55 hover:text-rose hover:bg-white/5 transition"
                    >
                        <Mail className="w-4 h-4" />
                    </a>
                )}
                <a
                    href={`/portal/book?service=facial_signature`}
                    target="_blank" rel="noreferrer"
                    title="Open booking page"
                    className="p-2 rounded-full text-white/55 hover:text-white hover:bg-white/5 transition"
                >
                    <ExternalLink className="w-4 h-4" />
                </a>
            </div>
        </div>
    );
}
